import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Shield, LogOut, ClipboardList, ChevronRight, Clock } from 'lucide-react';
import { tempGuestAPI } from '../../services/api';

const MODULE_LABELS = {
  ropa_intake: 'ROPA Intake',
  breach_intake: 'Breach Intake',
  dpia_intake: 'DPIA Intake',
  prescreen: 'Pre-Screen Assessment',
  readiness_assessment: 'Readiness Assessment',
  questionnaire: 'Questionnaire',
};

const MODULE_ROUTES = {
  ropa_intake: '/guest/ropa-intake',
  breach_intake: '/guest/breach-intake',
  dpia_intake: '/guest/dpia-intake',
  prescreen: '/guest/prescreen',
  readiness_assessment: '/guest/readiness-assessment',
  questionnaire: '/guest/questionnaire',
};

const STATUS_BADGE = {
  submitted:   { label: 'Submitted',   className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  in_progress: { label: 'In Progress', className: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  pending:     { label: 'Pending',     className: 'bg-slate-500/10 text-slate-400 border-slate-500/20' },
  expired:     { label: 'Expired',     className: 'bg-rose-500/10 text-rose-400 border-rose-500/20' },
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function GuestPortalPage() {
  const navigate = useNavigate();
  const [guestUser, setGuestUser] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);

  useEffect(() => {
    const raw = localStorage.getItem('guest_user');
    if (!raw) { navigate('/guest/login'); return; }
    const user = JSON.parse(raw);
    setGuestUser(user);
    fetchProfile();
    fetchSubmissions();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = async () => {
    try {
      const res = await tempGuestAPI.myProfile();
      const fresh = res.data;
      // Sync localStorage so portal always shows up-to-date modules
      const stored = JSON.parse(localStorage.getItem('guest_user') || '{}');
      const updated = { ...stored, ...fresh };
      localStorage.setItem('guest_user', JSON.stringify(updated));
      setGuestUser(updated);
    } catch {
      // Non-fatal — keep showing stale data
    }
  };

  const fetchSubmissions = async () => {
    try {
      const res = await tempGuestAPI.mySubmissions();
      setSubmissions(res.data.submissions || []);
    } catch {
      // Non-fatal
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('guest_mode');
    localStorage.removeItem('guest_user');
    navigate('/guest/login');
  };

  if (!guestUser) return null;

  const modules = guestUser.guest_modules || [];
  const expiresAt = guestUser.guest_expires_at;
  const isExpired = expiresAt && new Date(expiresAt) < new Date();

  // Group submissions by module for the history section
  const submissionsByModule = submissions.reduce((acc, s) => {
    const key = s.module || 'unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-violet-400" />
            <span className="text-white font-semibold text-sm">Assura</span>
            <span className="text-slate-600 text-sm">·</span>
            <span className="text-slate-400 text-sm">Guest Access</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-slate-500 text-sm hidden sm:block">{guestUser.email}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white gap-1.5"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Welcome */}
        <div>
          <h1 className="text-2xl font-bold text-white">Hi, {guestUser.full_name.split(' ')[0]}</h1>
          <p className="text-slate-400 mt-1">
            You have access to {modules.length === 1 ? 'complete a form' : `${modules.length} forms`} on Assura.
          </p>
          {expiresAt && (
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-full px-3 py-1">
              <Clock className="h-3 w-3" />
              {isExpired ? 'Access expired' : `Access expires ${formatDate(expiresAt)}`}
            </div>
          )}
        </div>

        {/* Module cards */}
        <div className="space-y-3">
          {modules.length === 0 ? (
            <Card className="bg-slate-900/30 border-slate-800 border-dashed">
              <CardContent className="p-8 text-center">
                <p className="text-slate-500 text-sm">No modules assigned to your account.</p>
              </CardContent>
            </Card>
          ) : (
            modules.map(module => {
              const moduleLabel = MODULE_LABELS[module] || module;
              const route = MODULE_ROUTES[module];
              // Shadow-table modules: invite status is the ground truth
              const inviteStatus = guestUser?.module_invite_statuses?.[module];
              const hasShadowStatus = inviteStatus !== undefined && inviteStatus !== null;
              // For modules without a shadow record (readiness_assessment) fall back to submission count
              const moduleSubmissions = submissionsByModule[module] || [];
              const submittedCount = moduleSubmissions.filter(s => s.status !== 'pending').length;

              const SUBMITTED_STATUSES = ['used', 'completed', 'submitted'];
              const alreadySubmitted = hasShadowStatus
                ? SUBMITTED_STATUSES.includes(inviteStatus)
                : submittedCount > 0;
              const isInProgress = hasShadowStatus && inviteStatus === 'in_progress';

              let subtitle = 'No submissions yet';
              if (isExpired) {
                subtitle = 'Your access has expired.';
              } else if (alreadySubmitted) {
                subtitle = 'Submitted — thank you!';
              } else if (isInProgress) {
                subtitle = 'In progress';
              } else if (!hasShadowStatus && submittedCount > 0) {
                subtitle = `${submittedCount} submission${submittedCount !== 1 ? 's' : ''}`;
              }

              const buttonLabel = alreadySubmitted
                ? 'Submitted'
                : isInProgress
                  ? 'Continue'
                  : 'Start form';

              return (
                <Card key={module} className="bg-slate-900/50 border-slate-800">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{moduleLabel}</p>
                        <p className="text-slate-400 text-sm mt-0.5">{subtitle}</p>
                      </div>
                      <Button
                        onClick={() => navigate(route)}
                        disabled={isExpired || alreadySubmitted}
                        className="bg-violet-600 hover:bg-violet-700 text-white gap-1.5 shrink-0 disabled:opacity-50"
                      >
                        {buttonLabel}
                        {!alreadySubmitted && <ChevronRight className="h-4 w-4" />}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Submission history */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <ClipboardList className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-medium text-slate-300">Your submissions</h2>
          </div>

          {loadingSubmissions ? (
            <div className="py-8 text-center text-slate-600 text-sm">Loading…</div>
          ) : submissions.filter(s => s.status !== 'pending').length === 0 ? (
            <Card className="bg-slate-900/30 border-slate-800 border-dashed">
              <CardContent className="p-8 text-center">
                <p className="text-slate-500 text-sm">No submissions yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Group by module — only show submitted/in_progress entries */}
              {Object.entries(
                submissions.filter(s => s.status !== 'pending').reduce((acc, s) => {
                  const key = s.module || 'unknown';
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(s);
                  return acc;
                }, {})
              ).map(([module, items]) => (
                <div key={module}>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                    {MODULE_LABELS[module] || module}
                  </p>
                  <div className="space-y-2">
                    {items.map((s, i) => {
                      const badge = STATUS_BADGE[s.status] || STATUS_BADGE.pending;
                      return (
                        <Card key={i} className="bg-slate-900/50 border-slate-800">
                          <CardContent className="p-4 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-white text-sm font-medium truncate">{s.form_name}</p>
                              <p className="text-slate-500 text-xs mt-0.5">{formatDate(s.submitted_at)}</p>
                            </div>
                            <Badge variant="outline" className={`shrink-0 ${badge.className}`}>
                              {badge.label}
                            </Badge>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
